import React, { useState, useCallback, useEffect, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useAuth } from 'hooks/api';
import { hasAction } from 'config/rolePermissions';
import { requestService } from 'api/services';
import { mapRequestDetailsToTicket } from 'api/mappers/requestToTicketMapper';
import TicketBasicInfo from './ticket-details/shared/TicketBasicInfo';
import TicketApprovalFlow from './ticket-details/shared/TicketApprovalFlow';
import TicketHistory from './ticket-details/shared/TicketHistory';
import TicketAttachments from './ticket-details/shared/TicketAttachments';
import TicketRelated from './ticket-details/shared/TicketRelated';
import { ApprovalDocumentModal, transformTicketToApprovalDocument } from './ticket-details/approval-document';
import { consolidationService } from '../../../api/services/das/consolidationService';
import type { DASTicketDetails, ConsolidatedChildItem } from 'api/types/dasTickets.types';
import type { ID, Request } from 'api/types';

// ============================================================================
// REQUEST STATUS LOOKUP - Digital Approval System
// ============================================================================
// Allows users to look up request status by entering a request number.
// Access control:
//   - das_admin: can view all requests
//   - Request creator: can view their own requests
//   - Approval chain users: can view requests where they are an assigned
//     approver, actual approver, delegate, or escalation target
//     (regardless of whether they have taken action)
// ============================================================================

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type TabId = 'basic-info' | 'approval-flow' | 'history' | 'attachments' | 'related';

interface TabConfig {
  id: TabId;
  label: string;
  icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
  badge?: number;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'approved': return '#10b981';
    case 'pre approved': return '#06b6d4';
    case 'rejected': return '#ef4444';
    case 'in review':
    case 'submitted':
    case 'pending': return '#f59e0b';
    case 'returned': return '#f97316';
    case 'on hold': return '#8b5cf6';
    case 'draft': return '#94a3b8';
    case 'cancelled': return '#64748b';
    default: return '#6b7280';
  }
};

const ACCESS_DENIED_MESSAGE = "Request not found or you don't have permission to view it";

// ============================================================================
// COMPONENT
// ============================================================================

const RequestStatusLookup: React.FC = () => {
  // State
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [ticketData, setTicketData] = useState<DASTicketDetails | null>(null);
  const [requestSummary, setRequestSummary] = useState<Request | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('basic-info');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const [showApprovalDocument, setShowApprovalDocument] = useState(false);
  const [consolidatedItems, setConsolidatedItems] = useState<ConsolidatedChildItem[]>([]);
  const [consolidatedItemsLoading, setConsolidatedItemsLoading] = useState(false);

  // Multi-match picker (when search term matches several requests via external reference)
  const [candidates, setCandidates] = useState<Request[]>([]);

  // External reference edit modal (das_admin only)
  const [showExternalRefModal, setShowExternalRefModal] = useState(false);
  const [externalRefInput, setExternalRefInput] = useState('');
  const [externalRefSaving, setExternalRefSaving] = useState(false);

  // Hooks
  const { user } = useAuth();

  // Configuration-driven action gates. All Track Request actions are now
  // driven by the role-permission framework (no hardcoded role names):
  //   • export             — gates the Generate Certificate button (PDF)
  //   • edit-external-ref  — gates the Edit External Reference pencil + modal
  //   • view-all           — used by the post-fetch defence-in-depth check
  //     below to mirror what the backend already enforces. The backend is
  //     the source of truth; this flag exists so users with view-all aren't
  //     also blocked by the involved-only client-side check.
  const canExport = useMemo(
    () => hasAction('request-status-lookup', 'export', user?.rolePermissions),
    [user]
  );
  const canEditExternalRef = useMemo(
    () => hasAction('request-status-lookup', 'edit-external-ref', user?.rolePermissions),
    [user]
  );
  const canViewAll = useMemo(
    () => hasAction('request-status-lookup', 'view-all', user?.rolePermissions),
    [user]
  );

  // Auto-dismiss warning message after 3 seconds
  useEffect(() => {
    if (!warningMessage) return;
    const timer = setTimeout(() => setWarningMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [warningMessage]);

  // Fetch consolidated items when ticket data is loaded (for consolidated requests)
  useEffect(() => {
    if (!ticketData) {
      setConsolidatedItems([]);
      return;
    }

    const fetchConsolidatedItems = async () => {
      const stageIndicatesConsolidation = ticketData.currentStage.toLowerCase().includes('consolidation');
      const hasConsolidatedCount = ticketData.consolidatedItemCount !== undefined && ticketData.consolidatedItemCount > 0;

      const shouldFetch = ticketData.isConsolidatedRequest || hasConsolidatedCount || stageIndicatesConsolidation;
      if (!shouldFetch) return;

      try {
        setConsolidatedItemsLoading(true);
        const response = await consolidationService.getChildren(ticketData.id.toString());
        const mappedItems: ConsolidatedChildItem[] = response.map((req: any) => ({
          id: req.id,
          ticketNo: req.requestNumber,
          subject: req.title,
          value: req.requestValue,
          status: req.currentStatus,
          createdByUserName: req.submittedByName || 'Unknown',
          preApprovedAt: req.preApprovedAt,
          preApprovedByName: req.preApprovedByName,
        }));
        setConsolidatedItems(mappedItems);
      } catch (err) {
        console.error('Failed to fetch consolidated items:', err);
      } finally {
        setConsolidatedItemsLoading(false);
      }
    };

    fetchConsolidatedItems();
  }, [ticketData]);

  // Generate approval document data (only for Approved/Rejected requests)
  const approvalDocumentData = useMemo(() => {
    if (!ticketData) return null;

    const hasConsolidatedItems = consolidatedItems.length > 0 || (ticketData.consolidatedItems?.length || 0) > 0;
    const hasConsolidatedCount = ticketData.consolidatedItemCount !== undefined && ticketData.consolidatedItemCount > 0;
    const stageIndicatesConsolidation = ticketData.currentStage.toLowerCase().includes('consolidation');
    const isConsolidated = ticketData.isConsolidatedRequest || hasConsolidatedItems || hasConsolidatedCount || stageIndicatesConsolidation;

    const ticketWithItems: DASTicketDetails = {
      ...ticketData,
      isConsolidatedRequest: isConsolidated,
      consolidatedItems: consolidatedItems.length > 0 ? consolidatedItems : ticketData.consolidatedItems,
      consolidatedItemCount: ticketData.consolidatedItemCount || consolidatedItems.length || ticketData.consolidatedItems?.length,
    };
    return transformTicketToApprovalDocument(ticketWithItems);
  }, [ticketData, consolidatedItems]);

  // Tab configuration
  const tabs: TabConfig[] = [
    { id: 'basic-info', label: 'Basic Information', icon: IconSystem.info },
    { id: 'approval-flow', label: 'Approval Flow', icon: IconSystem.users },
    { id: 'history', label: 'History', icon: IconSystem.clock },
    { id: 'attachments', label: 'Attachments', icon: IconSystem.file, badge: ticketData?.attachments?.length || 0 },
    { id: 'related', label: 'Related Requests', icon: IconSystem.link },
  ];

  // ==========================================================================
  // SEARCH HANDLER
  // ==========================================================================

  // Load full details for a specific request id, applying the same access check
  // used by the original search flow. Caller decides whether to setLoading.
  const loadRequestById = useCallback(async (id: ID, summary?: Request): Promise<boolean> => {
    if (!user) {
      setWarningMessage('Please wait, loading user data...');
      return false;
    }

    try {
      // Fetch full details (includes approvers for access check)
      const details = await requestService.getDetails(id);

      // Frontend access check: admin, creator, or any user in the approval chain
      const userId = user?.id?.toString();
      const submittedBy = summary?.submittedBy?.toString();
      const isCreator = submittedBy !== undefined && submittedBy === userId;
      const isInApprovalChain = details.approvers?.some(a =>
        a.approverUserId?.toString() === userId
        || a.actualApproverUserId?.toString() === userId
        || a.delegatedToUserId?.toString() === userId
        || a.escalatedToUserId?.toString() === userId
      ) ?? false;

      if (!canViewAll && !isCreator && !isInApprovalChain) {
        setErrorMessage(ACCESS_DENIED_MESSAGE);
        return false;
      }

      const mapped = mapRequestDetailsToTicket(details, user?.id);
      setTicketData(mapped);
      // If we don't have a summary (e.g. came from a refresh), synthesize one from details
      setRequestSummary(summary ?? {
        ...(details as unknown as Request),
      });
      setCandidates([]);
      setActiveTab('basic-info');
      setErrorMessage(null);
      return true;
    } catch (err) {
      setErrorMessage(ACCESS_DENIED_MESSAGE);
      return false;
    }
  }, [user, canViewAll]);

  const handleSearch = useCallback(async () => {
    const trimmed = searchInput.trim();
    if (!trimmed) {
      setWarningMessage('Please enter a request number or external reference');
      return;
    }

    if (!user) {
      setWarningMessage('Please wait, loading user data...');
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    setWarningMessage(null);
    setTicketData(null);
    setRequestSummary(null);
    setCandidates([]);
    setActiveTab('basic-info');

    try {
      // Lookup matches request_number OR external_reference_no
      const results = await requestService.lookup(trimmed);

      if (results.length === 0) {
        setErrorMessage(ACCESS_DENIED_MESSAGE);
        return;
      }

      if (results.length > 1) {
        // Multiple matches (only possible via external reference duplicates) — let user pick
        setCandidates(results);
        return;
      }

      // Exactly one match — load full details
      await loadRequestById(results[0].id, results[0]);
    } catch (err) {
      setErrorMessage(ACCESS_DENIED_MESSAGE);
    } finally {
      setLoading(false);
    }
  }, [searchInput, user, loadRequestById]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClear = () => {
    setSearchInput('');
    setTicketData(null);
    setRequestSummary(null);
    setErrorMessage(null);
    setActiveTab('basic-info');
    setConsolidatedItems([]);
    setShowApprovalDocument(false);
    setCandidates([]);
  };

  const handleOpenExternalRefModal = useCallback(() => {
    if (!ticketData) return;
    setExternalRefInput(ticketData.externalReferenceNo ?? '');
    setShowExternalRefModal(true);
  }, [ticketData]);

  const handleSaveExternalRef = useCallback(async () => {
    if (!ticketData) return;
    setExternalRefSaving(true);
    try {
      await requestService.updateExternalReference(
        ticketData.id,
        externalRefInput.trim() || null
      );
      // Re-fetch details so the new value and the audit row in History are both reflected.
      // requestSummary is preserved (request number doesn't change).
      await loadRequestById(ticketData.id, requestSummary ?? undefined);
      setShowExternalRefModal(false);
    } catch (e) {
      setWarningMessage('Failed to update external reference');
    } finally {
      setExternalRefSaving(false);
    }
  }, [ticketData, externalRefInput, requestSummary, loadRequestById]);

  // ==========================================================================
  // TAB CONTENT RENDERER
  // ==========================================================================

  const renderTabContent = () => {
    if (!ticketData) return null;
    switch (activeTab) {
      case 'basic-info':
        return (
          <div style={{ padding: '1.5rem' }}>
            <TicketBasicInfo
              ticket={ticketData}
              canEditExternalReference={canEditExternalRef}
              onEditExternalReference={handleOpenExternalRefModal}
            />
          </div>
        );
      case 'approval-flow':
        return (
          <div style={{ padding: '1.5rem' }}>
            <TicketApprovalFlow
              approvalHierarchy={ticketData.approvalHierarchy || []}
              ticketStatus={ticketData.currentStage}
            />
          </div>
        );
      case 'history':
        return (
          <div style={{ padding: '1.5rem' }}>
            <TicketHistory workflowHistory={ticketData.workflowHistory || []} />
          </div>
        );
      case 'attachments':
        return (
          <div style={{ padding: '1.5rem' }}>
            <TicketAttachments attachments={ticketData.attachments || []} />
          </div>
        );
      case 'related':
        return (
          <div style={{ padding: '1.5rem' }}>
            <TicketRelated
              relatedTickets={ticketData.relatedTickets || []}
              relatedTicketDetails={ticketData.relatedTicketDetails}
            />
          </div>
        );
      default:
        return null;
    }
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <div className="module-master-container with-tabs">
      {/* Spinner keyframes */}
      <style>{`
        @keyframes rsl-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .rsl-spinner { animation: rsl-spin 1s linear infinite; }
        .rsl-scroll-hidden { scrollbar-width: none; -ms-overflow-style: none; }
        .rsl-scroll-hidden::-webkit-scrollbar { display: none; }
      `}</style>

      {/* Warning Toast */}
      {warningMessage && (
        <div
          style={{
            position: 'fixed',
            top: '1.5rem',
            right: '1.5rem',
            zIndex: 10100,
            background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
            border: '1px solid #fbbf24',
            borderRadius: '0.5rem',
            padding: '0.75rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.625rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <IconSystem.warning size={16} color="#d97706" />
          <span style={{ fontSize: '0.8125rem', color: '#92400e', fontWeight: 500 }}>
            {warningMessage}
          </span>
          <button
            onClick={() => setWarningMessage(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.125rem', display: 'flex', color: '#92400e' }}
          >
            <IconSystem.xCircle size={14} />
          </button>
        </div>
      )}

      {/* ================================================================== */}
      {/* SEARCH CARD (fixed at top)                                         */}
      {/* ================================================================== */}
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '0.75rem',
          padding: '0.875rem 1.25rem',
          margin: '0.75rem 0.75rem 0 0.75rem',
          border: '1px solid var(--border-color)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          backdropFilter: 'blur(12px)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
        }}
      >
        <div
          style={{
            width: '2.25rem',
            height: '2.25rem',
            borderRadius: '0.5rem',
            background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <IconSystem.search size={16} color="white" />
        </div>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
          Track Request
        </h2>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flex: 1 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g., REQ-2026-0001 or external reference"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.625rem 2.25rem 0.625rem 1rem',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                outline: 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#2563eb';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-color)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
            {searchInput && !loading && (
              <button
                onClick={handleClear}
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-muted)',
                }}
                title="Clear"
              >
                <IconSystem.xCircle size={16} />
              </button>
            )}
          </div>
          <button
            onClick={handleSearch}
            disabled={loading || !searchInput.trim()}
            style={{
              padding: '0.625rem 1.25rem',
              background: loading || !searchInput.trim() ? '#93c5fd' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: loading || !searchInput.trim() ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s',
              boxShadow: loading || !searchInput.trim() ? 'none' : '0 1px 3px rgba(37, 99, 235, 0.3)',
              whiteSpace: 'nowrap',
            }}
          >
            {loading ? (
              <IconSystem.refresh size={16} className="rsl-spinner" />
            ) : (
              <IconSystem.search size={16} />
            )}
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {/* ================================================================== */}
      {/* LOADING / ERROR / EMPTY STATES                                     */}
      {/* ================================================================== */}
      {loading && (
        <div
          style={{
            background: 'var(--bg-primary)',
            borderRadius: '0.75rem',
            padding: '3rem',
            margin: '0.75rem',
            border: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            backdropFilter: 'blur(12px)',
            flex: 1,
            minHeight: 0,
          }}
        >
          <IconSystem.refresh size={32} color="#2563eb" className="rsl-spinner" />
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
            Looking up request details...
          </p>
        </div>
      )}

      {/* ================================================================== */}
      {/* ERROR STATE                                                        */}
      {/* ================================================================== */}
      {errorMessage && !loading && (
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(254, 242, 242, 0.9), rgba(254, 226, 226, 0.7))',
            border: '1px solid #fecaca',
            borderRadius: '0.75rem',
            padding: '1.25rem 1.5rem',
            margin: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            backdropFilter: 'blur(8px)',
            flex: 1,
            minHeight: 0,
          }}
        >
          <div
            style={{
              width: '2rem',
              height: '2rem',
              borderRadius: '50%',
              background: 'rgba(220, 38, 38, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <IconSystem.xCircle size={18} color="#dc2626" />
          </div>
          <span style={{ fontSize: '0.875rem', color: '#991b1b', fontWeight: 500 }}>
            {errorMessage}
          </span>
        </div>
      )}

      {/* ================================================================== */}
      {/* CANDIDATE PICKER (multi-match by external reference)               */}
      {/* ================================================================== */}
      {!loading && !errorMessage && !ticketData && candidates.length > 0 && (
        <div
          style={{
            background: 'var(--bg-primary)',
            borderRadius: '0.75rem',
            margin: '0.75rem',
            border: '1px solid var(--border-color)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
            backdropFilter: 'blur(12px)',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '1rem 1.25rem',
              borderBottom: '1px solid var(--border-color)',
              flexShrink: 0,
            }}
          >
            <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              {candidates.length} requests share this reference
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '0.125rem' }}>
              Pick one to view its details.
            </div>
          </div>
          <div className="rsl-scroll-hidden" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {candidates.map((c) => (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                onClick={() => loadRequestById(c.id, c)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    loadRequestById(c.id, c);
                  }
                }}
                style={{
                  padding: '0.875rem 1.25rem',
                  borderBottom: '1px solid var(--border-color)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  transition: 'background 0.15s',
                  userSelect: 'none',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-secondary)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                      {c.requestNumber}
                    </span>
                    <span
                      style={{
                        padding: '0.125rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        background: getStatusColor(c.currentStatus),
                        color: 'white',
                      }}
                    >
                      {c.currentStatus}
                    </span>
                    {c.externalReferenceNo && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        Ext. Ref: <strong style={{ color: 'var(--text-primary)' }}>{c.externalReferenceNo}</strong>
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: '0.8125rem',
                      color: 'var(--text-primary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c.title}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.125rem' }}>
                    {c.submittedByName || 'Unknown'}
                    {c.submittedAt && ` · ${new Date(c.submittedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`}
                  </div>
                </div>
                <IconSystem.chevronRight size={16} color="var(--text-muted)" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ================================================================== */}
      {/* EMPTY STATE (Initial)                                              */}
      {/* ================================================================== */}
      {!loading && !errorMessage && !ticketData && candidates.length === 0 && (
        <div
          style={{
            background: 'var(--bg-primary)',
            borderRadius: '0.75rem',
            padding: '3rem',
            margin: '0.75rem',
            border: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            backdropFilter: 'blur(12px)',
            flex: 1,
            minHeight: 0,
          }}
        >
          <div
            style={{
              width: '3.5rem',
              height: '3.5rem',
              borderRadius: '1rem',
              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1), rgba(29, 78, 216, 0.05))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconSystem.search size={24} color="#2563eb" />
          </div>
          <p style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--text-primary)', fontWeight: 600 }}>
            Search for a Request
          </p>
          <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-secondary)', textAlign: 'center', maxWidth: '360px' }}>
            Enter a request number or external reference number above to view its current status, approval flow, and history
          </p>
        </div>
      )}

      {/* ================================================================== */}
      {/* RESULTS SECTION                                                    */}
      {/* ================================================================== */}
      {ticketData && requestSummary && !loading && (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-primary)',
            borderRadius: '0.75rem',
            margin: '0.75rem',
            border: '1px solid var(--border-color)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
            backdropFilter: 'blur(12px)',
          }}
        >
          {/* Request Summary Header (pinned) */}
          <div
            style={{
              padding: '1.25rem 1.5rem',
              borderBottom: '1px solid var(--border-color)',
              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.03), rgba(29, 78, 216, 0.01))',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '1rem',
              }}
            >
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.375rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {requestSummary.requestNumber}
                  </h3>
                  <span
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '0.375rem',
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      background: getStatusColor(requestSummary.currentStatus),
                      color: 'white',
                    }}
                  >
                    {requestSummary.currentStatus}
                  </span>
                </div>
              </div>
              {approvalDocumentData && canExport && (
                <button
                  onClick={() => setShowApprovalDocument(true)}
                  disabled={consolidatedItemsLoading}
                  style={{
                    padding: '0.5rem 1rem',
                    background: consolidatedItemsLoading ? '#94a3b8' : '#1e40af',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: 'white',
                    cursor: consolidatedItemsLoading ? 'wait' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    opacity: consolidatedItemsLoading ? 0.7 : 1,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => {
                    if (!consolidatedItemsLoading) e.currentTarget.style.background = '#1e3a8a';
                  }}
                  onMouseLeave={(e) => {
                    if (!consolidatedItemsLoading) e.currentTarget.style.background = '#1e40af';
                  }}
                >
                  <IconSystem.file size={16} />
                  {consolidatedItemsLoading ? 'Loading...' : 'Generate Certificate'}
                </button>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div
            style={{
              display: 'flex',
              borderBottom: '1px solid var(--border-color)',
              padding: '0 1.5rem',
              overflowX: 'auto',
              overflowY: 'hidden',
              flexShrink: 0,
              background: 'var(--bg-primary)',
            }}
          >
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '0.875rem 1.25rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: activeTab === tab.id ? 'var(--primary)' : 'var(--text-secondary)',
                    borderBottom: activeTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
                    fontWeight: activeTab === tab.id ? 600 : 400,
                    fontSize: '0.8125rem',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Icon size={15} />
                  {tab.label}
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span
                      style={{
                        background: activeTab === tab.id ? 'var(--primary)' : 'var(--text-muted)',
                        color: 'white',
                        padding: '0.0625rem 0.4375rem',
                        borderRadius: '1rem',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        minWidth: '1.25rem',
                        textAlign: 'center',
                      }}
                    >
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab Content (scrollable, hidden scrollbar) */}
          <div className="rsl-scroll-hidden" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {renderTabContent()}
          </div>
        </div>
      )}

      {/* Approval Document / Certificate Modal */}
      {approvalDocumentData && (
        <ApprovalDocumentModal
          isOpen={showApprovalDocument}
          onClose={() => setShowApprovalDocument(false)}
          data={approvalDocumentData}
        />
      )}

      {/* External Reference edit modal — gated by the `edit-external-ref`
          action on `request-status-lookup` (configurable per role via
          the Role Permissions UI). */}
      {showExternalRefModal && canEditExternalRef && ticketData && (
        <>
          <div
            onClick={() => !externalRefSaving && setShowExternalRefModal(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1001,
              background: 'var(--bg-primary)',
              borderRadius: '0.75rem',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              width: '90%',
              maxWidth: '480px',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid var(--border-color)',
            }}
          >
            <div
              style={{
                padding: '0.875rem 1.25rem',
                borderBottom: '1px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.625rem',
              }}
            >
              <IconSystem.edit size={16} color="#2563eb" />
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                Edit External Reference Number
              </h3>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.375rem' }}>
                Request: <strong style={{ color: 'var(--text-primary)' }}>{ticketData.ticketNo}</strong>
              </div>
              <input
                type="text"
                autoFocus
                value={externalRefInput}
                onChange={(e) => setExternalRefInput(e.target.value)}
                maxLength={100}
                placeholder="Leave blank to clear"
                disabled={externalRefSaving}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !externalRefSaving) {
                    handleSaveExternalRef();
                  } else if (e.key === 'Escape' && !externalRefSaving) {
                    setShowExternalRefModal(false);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '0.625rem 0.875rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-secondary)', marginTop: '0.375rem' }}>
                Maximum 100 characters. Changes are recorded in the workflow history.
              </div>
            </div>
            <div
              style={{
                padding: '0.75rem 1.25rem',
                borderTop: '1px solid var(--border-color)',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.5rem',
              }}
            >
              <button
                type="button"
                onClick={() => setShowExternalRefModal(false)}
                disabled={externalRefSaving}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  cursor: externalRefSaving ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveExternalRef}
                disabled={externalRefSaving}
                style={{
                  padding: '0.5rem 1rem',
                  background: externalRefSaving ? '#93c5fd' : '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  cursor: externalRefSaving ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                }}
              >
                {externalRefSaving && <IconSystem.refresh size={14} className="rsl-spinner" />}
                {externalRefSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default RequestStatusLookup;
